import { useMemo, useState } from 'react';

import dayjs from 'dayjs';

import useRedirect from 'hooks/use-redirect';
import type { OrganizationJob } from 'state/remote/types';
import useBlockFreelancerFromOrganization from 'state/remote/remote-organization/use-block-freelancer-from-organization';
import useBlockFreelancerInLocation from 'state/remote/remote-organization/use-block-freelancer-in-location';
import useCancelJobs from 'state/remote/remote-organization/use-cancel-jobs';
import useFavoriteFreelancerForLocation from 'state/remote/remote-organization/use-favorite-freelancer-for-location';
import useFavoriteFreelancerForOrganization from 'state/remote/remote-organization/use-favorite-freelancer-for-organization';
import useInviteEveryone from 'state/remote/remote-organization/use-invite-everyone';
import useMarkFreelancerAsNoshow from 'state/remote/remote-organization/use-mark-freelancer-as-noshow';
import useUnblockFreelancerFromOrganization from 'state/remote/remote-organization/use-unblock-freelancer-from-organization';
import useUnblockFreelancerInLocation from 'state/remote/remote-organization/use-unblock-freelancer-in-location';
import useUnfavoriteFreelancerForLocation from 'state/remote/remote-organization/use-unfavorite-freelancer-for-location';
import useUnfavoriteFreelancerForOrganization from 'state/remote/remote-organization/use-unfavorite-freelancer-for-organization';
import useRateUserJob from 'state/remote/remote-organization/use-rate-user-job';
import { useOrganizationWeekPlanner } from 'state/context/organization/OrganizationWeekPlanner';

const EDIT_PERIOD_COMPLETED_JOBS = 14;
const ASSIGN_PERIOD_COMPLETED_JOBS = 30;
const HOURS_PERIOD_COMPLETED_JOBS = 30;
const RATING_PERIOD_COMPLETED_JOBS = 90;
const DAYJS_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const COMPETENCE_CHOICES = {
  'Activiteiten Begeleider': 'abgl',
  'Buitenschoolse opvang': 'bso',
  Kantoor: 'kantoor',
  'KDV Babygroep': 'baby',
  'KDV Dreumes': 'dreumes',
  'KDV Peuters': 'peuter',
  'KDV Verticale groep': 'verticaal',
  Leidingevende: 'lvd,',
  'Pedagogisch Coach': 'pedagoog',
  'Persoonlijk Begeleider': 'pbgl',
  Peuterspeelzaal: 'speelzaal',
  'VVE / Voorschool': 'vve',
  'Woon Begeleider': 'wbgl',
};

interface UseJobPopoverType {
  cancelJobDialogVariant: 'explain' | 'compensate' | undefined;
  disaplyAssignFreelancerAction: boolean;
  displayCancelAction: boolean;
  displayConfirmAction: boolean;
  displayEditAction: boolean;
  displayFeedbackAction: boolean;
  displayHoursAction: boolean;
  displayConvertToUrgentAction: boolean;
  displayNoShowAction: boolean;
  displaySplitAction: boolean;
  isCancelDialogOpen: boolean;
  isFeedbackDialogOpen: boolean;
  isNoshowDialogOpen: boolean;
  onAgreementClicked: () => void;
  // onAssignJobToFreelancerClicked: () => void;
  onAssignmentDetailsClicked: () => void;
  onBlockFreelancerFromOrganizationClicked: () => void;
  onBlockFreelancerInLocationClicked: () => void;
  onCancelClicked: () => void;
  onCancelDialogAcceptClicked: (reason?: string) => void;
  onConfirmClicked: () => void;
  onEditClicked: () => void;
  onFavoriteFreelancerForLocationClicked: () => void;
  onFavoriteFreelancerForOrganizationClicked: () => void;
  onFeedbackClicked: () => void;
  onFeedbackDialogAcceptClicked: (values: {
    feedback: string;
    rating: number;
  }) => void;
  onHoursClicked: () => void;
  onInviteEveryoneClicked: () => void;
  onNoShowClicked: () => void;
  onNoshowDialogAcceptClicked: (reason: string) => void;
  onRepeatAssignmentsClicked: () => void;
  onSplitClicked: () => void;
  onUnblockFreelancerFromOrganizationClicked: () => void;
  onUnblockFreelancerInLocationClicked: () => void;
  onUnfavoriteFreelancerForLocationClicked: () => void;
  onUnfavoriteFreelancerForOrganizationClicked: () => void;
  setIsCancelDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFeedbackDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsNoshowDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const useJobPopover = (job: OrganizationJob): UseJobPopoverType => {
  const { organizationPlannerSearchParams } = useOrganizationWeekPlanner();

  const startDate = useMemo(() => {
    return dayjs(`${job.date} ${job.start}`, DAYJS_FORMAT);
  }, [job]);

  const endDate = useMemo(() => {
    return dayjs(`${job.date} ${job.end}`, DAYJS_FORMAT);
  }, [job]);
  const displayNoShowAction = useMemo(() => {
    const currentDate = dayjs();

    return (
      startDate.isBefore(currentDate) &&
      endDate.isAfter(currentDate) &&
      !!job.assigned_to
    );
  }, [endDate, job.assigned_to, startDate]);

  const disaplyAssignFreelancerAction = useMemo(() => {
    const currentDate = dayjs();
    const deadlineDate = dayjs(endDate, DAYJS_FORMAT).add(
      ASSIGN_PERIOD_COMPLETED_JOBS,
      'day',
    );

    return !job.assigned_to && currentDate.isBefore(deadlineDate);
  }, [endDate, job.assigned_to]);

  const displayConfirmAction = useMemo(() => {
    return (
      startDate.isAfter(dayjs()) &&
      !job.assigned_to &&
      !job.accept_now &&
      job.is_interest_shown
    );
  }, [job.accept_now, job.assigned_to, job.is_interest_shown, startDate]);

  const displayEditAction = useMemo(() => {
    const deadlineDate = dayjs(endDate, DAYJS_FORMAT).add(
      EDIT_PERIOD_COMPLETED_JOBS,
      'day',
    );

    return dayjs().isBefore(deadlineDate);
  }, [endDate]);

  const displaySplitAction = useMemo(() => {
    const deadlineDate = dayjs(endDate, DAYJS_FORMAT).add(
      EDIT_PERIOD_COMPLETED_JOBS,
      'day',
    );

    return dayjs().isBefore(deadlineDate) && !!job.assigned_to;
  }, [endDate, job.assigned_to]);

  const displayConvertToUrgentAction = useMemo(() => {
    return (
      (!job.assigned_to &&
        job.invite_only &&
        startDate.isAfter(dayjs()) &&
        job.accept_now) ||
      (!job.assigned_to && job.is_interest && !job.is_interest_shown)
    );
  }, [
    job.accept_now,
    job.assigned_to,
    job.invite_only,
    job.is_interest,
    job.is_interest_shown,
    startDate,
  ]);

  const displayFeedbackAction = useMemo(() => {
    const deadlineDate = dayjs(endDate, DAYJS_FORMAT).add(
      RATING_PERIOD_COMPLETED_JOBS,
      'day',
    );
    const currentDate = dayjs();

    return (
      !!job.assigned_to &&
      startDate.isBefore(currentDate) &&
      currentDate.isBefore(deadlineDate)
    );
  }, [endDate, job.assigned_to, startDate]);

  const displayHoursAction = useMemo(() => {
    const deadlineDate = dayjs(endDate, DAYJS_FORMAT).add(
      HOURS_PERIOD_COMPLETED_JOBS,
      'day',
    );
    const currentDate = dayjs();

    return (
      !!job.assigned_to &&
      startDate.isBefore(currentDate) &&
      currentDate.isBefore(deadlineDate)
    );
  }, [endDate, job.assigned_to, startDate]);

  const cancelJobMutation = useCancelJobs();
  const blockFreelancerFromOrganizationMutation =
    useBlockFreelancerFromOrganization();
  const unBlockFreelancerFromOrganizationMutation =
    useUnblockFreelancerFromOrganization();
  const redirect = useRedirect();
  const blockFreelancerInLocationMutation = useBlockFreelancerInLocation();
  const unBlockFreelancerInLocationMutation = useUnblockFreelancerInLocation();

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);

  const onCancelClicked = () => {
    setIsCancelDialogOpen(true);
  };

  const onCancelDialogAcceptClicked = (reason?: string) => {
    cancelJobMutation.mutate({ jobs: [job.id], reason });
    organizationPlannerSearchParams.resetPageNumber();
    setIsCancelDialogOpen(false);
  };

  const onBlockFreelancerFromOrganizationClicked = () => {
    if (job.assigned_to) {
      blockFreelancerFromOrganizationMutation.mutate({
        user: job.assigned_to.id,
      });
    }
  };

  // const onAssignJobToFreelancerClicked = () => {
  //   redirect(`/organisation/job/${job.id}/assign/`);
  // };

  const onConfirmClicked = () => {
    redirect(`/organisation/job/${job.id}/confirm/`);
  };
  const onRepeatAssignmentsClicked = () => {
    const competence = job.competence as keyof typeof COMPETENCE_CHOICES;
    const queryParams = {
      competence: COMPETENCE_CHOICES[competence],
      date: job.date,
      end: dayjs(job.end, 'HH:mm:ss').format('HH:mm'),
      location: job.location.id.toString(),
      pause: job.pause.toString(),
      start: dayjs(job.start, 'HH:mm:ss').format('HH:mm'),
    };

    if (job.assigned_to) {
      redirect(
        `/organisation/jobs/schedule/${
          job.assigned_to.id
        }/?${new URLSearchParams(queryParams)}`,
      );
    }
  };

  const onEditClicked = () => {
    redirect(`/organisation/job/${job.id}/edit`);
  };

  const onAgreementClicked = () => {
    redirect(`/organisation/job/${job.id}/contract/`);
  };

  const onHoursClicked = () => {
    redirect(`/organisation/job/${job.id}/hours/`);
  };

  const onUnblockFreelancerFromOrganizationClicked = () => {
    if (job.assigned_to) {
      unBlockFreelancerFromOrganizationMutation.mutate({
        userId: job.assigned_to.id,
      });
    }
  };
  const onUnblockFreelancerInLocationClicked = () => {
    if (job.assigned_to) {
      unBlockFreelancerInLocationMutation.mutate({
        locationId: job.location.id,
        userId: job.assigned_to.id,
      });
    }
  };
  const onBlockFreelancerInLocationClicked = () => {
    if (job.assigned_to) {
      blockFreelancerInLocationMutation.mutate({
        data: { user: job.assigned_to.id },
        locationId: job.location.id,
      });
    }
  };
  const onSplitClicked = () => {
    redirect(`/organisation/job/${job.id}/split-shifts/`);
  };

  const onFeedbackClicked = () => {
    setIsFeedbackDialogOpen(true);
  };

  const cancelJobDialogVariant = useMemo(():
    | undefined
    | 'explain'
    | 'compensate' => {
    const startDatetimeBeforeOneDay = startDate.add(-1, 'day');
    const startDatetimeAfterOneHour = startDate.add(1, 'h');

    if (job.assigned_to) {
      if (
        startDatetimeBeforeOneDay.isBefore(dayjs()) &&
        startDatetimeAfterOneHour.isAfter(dayjs())
      ) {
        return 'compensate';
      }

      if (startDatetimeBeforeOneDay.isAfter(dayjs())) {
        return 'explain';
      }
    }

    return undefined;
  }, [job.assigned_to, startDate]);

  const displayCancelAction = useMemo(() => {
    const startDatetime = dayjs(
      `${job.date} ${job.start}`,
      'YYYY-MM-DD HH:mm:ss',
    );
    return dayjs().isBefore(startDatetime);
  }, [job]);
  const favoriteFreelancerForOrganizationMutation =
    useFavoriteFreelancerForOrganization();
  const unfavoriteFreelancerForOrganizationMutation =
    useUnfavoriteFreelancerForOrganization();
  const favoriteFreelancerForLocationMutation =
    useFavoriteFreelancerForLocation();
  const unfavoriteFreelancerForLocationMutation =
    useUnfavoriteFreelancerForLocation();

  const onFavoriteFreelancerForOrganizationClicked = () => {
    if (job.assigned_to) {
      favoriteFreelancerForOrganizationMutation.mutate({
        organizationId: job.organisation.id,
        userId: job.assigned_to.id,
      });
    }
  };
  const onUnfavoriteFreelancerForOrganizationClicked = () => {
    if (job.assigned_to) {
      unfavoriteFreelancerForOrganizationMutation.mutate({
        organizationId: job.organisation.id,
        userId: job.assigned_to.id,
      });
    }
  };

  const onFavoriteFreelancerForLocationClicked = () => {
    if (job.assigned_to) {
      favoriteFreelancerForLocationMutation.mutate({
        locationId: job.location.id,
        userId: job.assigned_to.id,
      });
    }
  };
  const onUnfavoriteFreelancerForLocationClicked = () => {
    if (job.assigned_to) {
      unfavoriteFreelancerForLocationMutation.mutate({
        locationId: job.location.id,
        userId: job.assigned_to.id,
      });
    }
  };

  const [isNoshowDialogOpen, setIsNoshowDialogOpen] = useState(false);

  const noshowMutation = useMarkFreelancerAsNoshow();
  const onNoshowDialogAcceptClicked = (reason: string) => {
    noshowMutation.mutate({
      data: {
        reason,
      },
      jobId: job.id,
    });
  };

  const onNoShowClicked = () => {
    setIsNoshowDialogOpen(true);
  };

  const onAssignmentDetailsClicked = () => {
    redirect(`/organisation/planner/jobs/${job.id}`, {
      shouldOpenNewTab: true,
    });
  };

  const inviteEveryoneMutatio = useInviteEveryone();
  const onInviteEveryoneClicked = () => {
    inviteEveryoneMutatio.mutate({
      jobId: job.id,
    });
  };

  const rateUserJobMutation = useRateUserJob();
  const onFeedbackDialogAcceptClicked = (values: {
    feedback: string;
    rating: number;
  }) => {
    rateUserJobMutation.mutate({
      feedback: values.feedback,
      jobId: job.id,
      rating: values.rating,
    });
    setIsFeedbackDialogOpen(false);
  };

  return {
    cancelJobDialogVariant,
    disaplyAssignFreelancerAction,
    displayCancelAction,
    displayConfirmAction,
    displayConvertToUrgentAction,
    displayEditAction,
    displayFeedbackAction,
    displayHoursAction,
    displayNoShowAction,
    displaySplitAction,
    isCancelDialogOpen,
    isFeedbackDialogOpen,
    isNoshowDialogOpen,
    onAgreementClicked,
    // onAssignJobToFreelancerClicked,
    onAssignmentDetailsClicked,
    onBlockFreelancerFromOrganizationClicked,
    onBlockFreelancerInLocationClicked,
    onCancelClicked,
    onCancelDialogAcceptClicked,
    onConfirmClicked,
    onEditClicked,
    onFavoriteFreelancerForLocationClicked,
    onFavoriteFreelancerForOrganizationClicked,
    onFeedbackClicked,
    onFeedbackDialogAcceptClicked,
    onHoursClicked,
    onInviteEveryoneClicked,
    onNoShowClicked,
    onNoshowDialogAcceptClicked,
    onRepeatAssignmentsClicked,
    onSplitClicked,
    onUnblockFreelancerFromOrganizationClicked,
    onUnblockFreelancerInLocationClicked,
    onUnfavoriteFreelancerForLocationClicked,
    onUnfavoriteFreelancerForOrganizationClicked,
    setIsCancelDialogOpen,
    setIsFeedbackDialogOpen,
    setIsNoshowDialogOpen,
  };
};

export default useJobPopover;
